import { readClarityWorkspaceRoute, toClaritySectionUrl } from './clarity-workspace-route.util';
import { CLARITY_DOCUMENT_URL_MAX_LENGTH } from './constants/clarity-document-url-max-length.constant';
import { CLARITY_DOCUMENTS_ROUTE_SEGMENT } from './constants/clarity-documents-route.constant';
import type { ClarityDocumentShareTarget } from './models/clarity-document-share-target.model';
import { parseClarityWorkspaceLocation } from './parse-clarity-workspace-location.util';

const PATH_SEPARATOR = '/';
const DOCUMENT_URL_PROTOCOLS: ReadonlySet<string> = new Set(['http:', 'https:']);
const DOCUMENT_PATH_SEGMENT_COUNT = 4;

function pathSegments(path: string): string[] {
	return path.split(PATH_SEPARATOR).filter(segment => segment !== '');
}

function toSectionedPath(pathname: string, sectionPath: string): string {
	const [firstSegment] = pathSegments(pathname);

	return firstSegment === sectionPath ? pathname : toClaritySectionUrl(sectionPath, pathname);
}

function isOnAllowedOrigin(address: URL, allowedOrigin: string | undefined): boolean {
	if (allowedOrigin === undefined) {
		return true;
	}

	return address.origin === URL.parse(allowedOrigin)?.origin;
}

export function readClarityDocumentUrl(
	href: string,
	sectionPath: string,
	allowedOrigin?: string,
): ClarityDocumentShareTarget | null {
	if (href.length > CLARITY_DOCUMENT_URL_MAX_LENGTH) {
		return null;
	}

	const address = URL.parse(href);

	if (address === null || !DOCUMENT_URL_PROTOCOLS.has(address.protocol)) {
		return null;
	}

	if (!isOnAllowedOrigin(address, allowedOrigin)) {
		return null;
	}

	const path = toSectionedPath(address.pathname, sectionPath);

	if (pathSegments(path).length !== DOCUMENT_PATH_SEGMENT_COUNT) {
		return null;
	}

	const route = readClarityWorkspaceRoute(`${path}${address.search}`, sectionPath);

	if (!route.isWorkspaceUrl) {
		return null;
	}

	const location = parseClarityWorkspaceLocation(route.params, route.search);

	if (location.tab !== CLARITY_DOCUMENTS_ROUTE_SEGMENT || location.domainId === null || location.doc === null) {
		return null;
	}

	return { domainId: location.domainId, fileId: location.doc, page: location.page };
}
